import React, { useState } from 'react';
import axios from '../config/axios';
import { vatValidationService } from '../services/vatValidationService';
import { BulkVatValidationResult } from '../services/vatValidationService';

interface BulkOperationsProps {
  entityType: 'users' | 'contracts' | 'pricing-headers' | 'customers' | 'companies';
  selectedIds: (number | string)[];
  onOperationComplete: () => void;
  onClearSelection: () => void;
}

interface BulkOperationResult {
  success_count: number;
  error_count: number;
  total_processed: number;
  errors: Array<{ id: number | string; error: string }>;
  warnings: Array<{ id: number | string; warning: string }>;
}

const BulkOperations: React.FC<BulkOperationsProps> = ({
  entityType,
  selectedIds,
  onOperationComplete,
  onClearSelection
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [showImportForm, setShowImportForm] = useState(false);
  const [updateData, setUpdateData] = useState<{ [key: string]: any }>({});
  const [importFile, setImportFile] = useState<File | null>(null);
  const [operationResult, setOperationResult] = useState<BulkOperationResult | null>(null);
  const [vatResults, setVatResults] = useState<BulkVatValidationResult[] | null>(null);
  const [showVatResults, setShowVatResults] = useState(false);

  const handleBulkUpdate = async () => {
    if (Object.keys(updateData).length === 0) {
      alert('Please specify at least one field to update');
      return;
    }

    setIsLoading(true);
    try {
      const endpoint = entityType === 'pricing-headers' 
        ? "/api/v1/bulk/pricing-headers/update"
        : `/api/v1/bulk/${entityType}/update`;

      const response = await axios.post(endpoint, {
        entity_ids: selectedIds,
        update_data: updateData
      });

      setOperationResult(response.data);
      setShowUpdateForm(false);
      setUpdateData({});
      onOperationComplete();
    } catch (error) {
      console.error('Bulk update error:', error);
      alert('Error performing bulk update');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkActivate = async (activate: boolean) => {
    setIsLoading(true);
    try {
      const response = await axios.post("/api/v1/bulk/activate", {
        entity_type: entityType,
        entity_ids: selectedIds,
        activate
      });

      setOperationResult(response.data);
      onOperationComplete();
    } catch (error) {
      console.error('Bulk activate/deactivate error:', error);
      alert(`Error ${activate ? 'activating' : 'deactivating'} items`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkDelete = async (softDelete: boolean = true) => {
    if (!window.confirm(`Are you sure you want to ${softDelete ? 'deactivate' : 'permanently delete'} ${selectedIds.length} items?`)) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`/api/v1/bulk/${entityType}/delete`, {
        entity_ids: selectedIds,
        soft_delete: softDelete
      });

      setOperationResult(response.data);
      onOperationComplete();
    } catch (error) {
      console.error('Bulk delete error:', error);
      alert('Error performing bulk delete');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Please select a file to import');
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(`/api/v1/bulk/import/${entityType}/csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setOperationResult(response.data);
      setShowImportForm(false);
      setImportFile(null);
      onOperationComplete();
    } catch (error) {
      console.error('Import error:', error);
      alert('Error importing data');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`/api/v1/bulk/templates/${entityType}/csv`);
      const csvContent = response.data.csv_header;
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${entityType}_template.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Error downloading template');
    }
  };

  const handleBulkVatCheck = async () => {
    if (entityType !== 'contracts') return;
    
    setIsLoading(true);
    try {
      // Convert selectedIds to strings for contract IDs
      const contractIds = selectedIds.map(id => id.toString());
      
      const result = await vatValidationService.checkContractLinesVat(contractIds);
      setVatResults(result.results);
      setShowVatResults(true);
    } catch (error) {
      console.error('VAT check error:', error);
      alert('Error checking VAT status');
    } finally {
      setIsLoading(false);
    }
  };

  const getUpdateFields = () => {
    const fieldConfigs: { [key: string]: Array<{ key: string; label: string; type: string }> } = {
      users: [
        { key: 'is_active', label: 'Active', type: 'boolean' },
        { key: 'verification', label: 'Verified', type: 'boolean' },
        { key: 'preferred_language', label: 'Preferred Language', type: 'text' },
        { key: 'phone', label: 'Phone', type: 'text' }
      ],
      contracts: [
        { key: 'status', label: 'Status', type: 'text' },
        { key: 'description', label: 'Description', type: 'text' }
      ],
      'pricing-headers': [
        { key: 'is_active', label: 'Active', type: 'boolean' },
        { key: 'description', label: 'Description', type: 'text' }
      ],
      customers: [
        { key: 'is_active', label: 'Active', type: 'boolean' },
        { key: 'customer_type', label: 'Customer Type', type: 'text' }
      ],
      companies: [
        { key: 'is_active', label: 'Active', type: 'boolean' },
        { key: 'description', label: 'Description', type: 'text' }
      ]
    };

    return fieldConfigs[entityType] || [];
  };

  const renderUpdateField = (field: { key: string; label: string; type: string }) => {
    switch (field.type) {
      case 'boolean':
        return (
          <select
            value={updateData[field.key] || ''}
            onChange={(e) => setUpdateData(prev => ({
              ...prev,
              [field.key]: e.target.value === 'true' ? true : e.target.value === 'false' ? false : undefined
            }))}
            className="w-full p-2 border border-gray-300 rounded-md"
          >
            <option value="">No change</option>
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        );
      default:
        return (
          <input
            type="text"
            value={updateData[field.key] || ''}
            onChange={(e) => setUpdateData(prev => ({
              ...prev,
              [field.key]: e.target.value || undefined
            }))}
            className="w-full p-2 border border-gray-300 rounded-md"
            placeholder={`Enter ${field.label.toLowerCase()}`}
          />
        );
    }
  };

  if (selectedIds.length === 0) {
    return (
      <div className="bg-gray-50 p-4 rounded-lg">
        <p className="text-gray-600">Select items to perform bulk operations</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h3 className="text-lg font-semibold mb-4">
        Bulk Operations ({selectedIds.length} items selected)
      </h3>

      {/* Operation Buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setShowUpdateForm(true)}
          disabled={isLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          Bulk Update
        </button>
        
        <button
          onClick={() => handleBulkActivate(true)}
          disabled={isLoading}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
        >
          Activate
        </button>
        
        <button
          onClick={() => handleBulkActivate(false)}
          disabled={isLoading}
          className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50"
        >
          Deactivate
        </button>
        
        {(entityType === 'users' || entityType === 'customers' || entityType === 'contracts') && (
          <button
            onClick={() => handleBulkDelete(true)}
            disabled={isLoading}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            Soft Delete
          </button>
        )}
        
        {(entityType === 'customers' || entityType === 'contracts') && (
          <button
            onClick={() => handleBulkDelete(false)}
            disabled={isLoading}
            className="px-4 py-2 bg-red-800 text-white rounded-md hover:bg-red-900 disabled:opacity-50"
          >
            Hard Delete
          </button>
        )}

        {entityType === 'contracts' && (
          <button
            onClick={handleBulkVatCheck}
            disabled={isLoading}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
          >
            Check VAT Status
          </button>
        )}

        <button
          onClick={() => setShowImportForm(true)}
          disabled={isLoading}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
        >
          Import Data
        </button>
        
        <button
          onClick={onClearSelection}
          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
        >
          Clear Selection
        </button>
      </div>

      {/* Update Form Modal */}
      {showUpdateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h4 className="text-lg font-semibold mb-4">Bulk Update</h4>
            
            <div className="space-y-4">
              {getUpdateFields().map((field) => (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                  </label>
                  {renderUpdateField(field)}
                </div>
              ))}
            </div>
            
            <div className="flex gap-2 mt-6">
              <button
                onClick={handleBulkUpdate}
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? 'Updating...' : 'Update'}
              </button>
              <button
                onClick={() => setShowUpdateForm(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Form Modal */}
      {showImportForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h4 className="text-lg font-semibold mb-4">Import Data</h4>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CSV File
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <button
                onClick={downloadTemplate}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Download Template
              </button>
            </div>
            
            <div className="flex gap-2 mt-6">
              <button
                onClick={handleImport}
                disabled={isLoading || !importFile}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
              >
                {isLoading ? 'Importing...' : 'Import'}
              </button>
              <button
                onClick={() => setShowImportForm(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VAT Results Modal */}
      {showVatResults && vatResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h4 className="text-lg font-semibold mb-4">VAT Validation Results</h4>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contract ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VAT Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tax Rate</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vatResults.map((result, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{result.contract_id || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{result.customer_id || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{result.customer_country}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{result.vat_number_normalized || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          !result.checked ? 'bg-yellow-100 text-yellow-800' :
                          result.is_valid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {!result.checked ? 'Not Checked' : result.is_valid ? 'Valid' : 'Invalid'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {result.tax_rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setShowVatResults(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowVatResults(false);
                  onClearSelection();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Close & Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Operation Result */}
      {operationResult && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-semibold mb-2">Operation Result</h4>
          <div className="text-sm space-y-1">
            <p>Success: {operationResult.success_count}</p>
            <p>Errors: {operationResult.error_count}</p>
            <p>Total Processed: {operationResult.total_processed}</p>
            
            {operationResult.errors.length > 0 && (
              <div className="mt-2">
                <p className="font-medium text-red-600">Errors:</p>
                <ul className="list-disc list-inside text-red-600">
                  {operationResult.errors.slice(0, 5).map((error, index) => (
                    <li key={index}>ID {error.id}: {error.error}</li>
                  ))}
                  {operationResult.errors.length > 5 && (
                    <li>... and {operationResult.errors.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}
            
            {operationResult.warnings.length > 0 && (
              <div className="mt-2">
                <p className="font-medium text-yellow-600">Warnings:</p>
                <ul className="list-disc list-inside text-yellow-600">
                  {operationResult.warnings.slice(0, 3).map((warning, index) => (
                    <li key={index}>ID {warning.id}: {warning.warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          <button
            onClick={() => setOperationResult(null)}
            className="mt-2 px-3 py-1 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

export default BulkOperations;